import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// --- Services ---
import { LocalStorageService } from '@services/local-storage.service';
import { SessionStorageService } from '@services/session-storage.service';

// --- Utils ---
import { isRefreshTokenExpired } from '@utils/auth-session.util';
import { authNavigation } from '@utils/redirect-to-auth.util';

// --- Interfaces ---
import type { IAuth } from '@interfaces/auth';

export interface IAuthState extends IAuth {}

export const INITIAL_STATE: IAuthState = {
	rememberMe: false,
	isAuthenticated: false,
};

@Injectable({
	providedIn: 'root',
})
export class AuthStore {
	private _authState = new BehaviorSubject<IAuthState>(
		this.resolveInitialState()
	);
	readonly authState$ = this._authState.asObservable();

	constructor(
		private localStorageService: LocalStorageService,
		private sessionStorageService: SessionStorageService
	) {}

	get authStateStorage(): IAuthState | null {
		return (
			this.localStorageService.get('auth') ||
			this.sessionStorageService.get('auth')
		);
	}

	login({ user, rememberMe }: IAuthState) {
		const state = this._authState.value;

		if (user?.accessToken) {
			user.token = user.accessToken;
			delete user.accessToken;
		}

		// Merge with the previous user. auth/refresh often returns only tokens;
		// replacing the whole object wiped profileId/photo/name and left the shell
		// looking like a visitor while the API token was still valid.
		const mergedUser = user
			? ({ ...(state.user ?? {}), ...user } as IAuthState['user'])
			: user;

		const data = {
			...state,
			user: mergedUser,
			rememberMe: state.rememberMe || rememberMe,
			isAuthenticated: true,
		};

		this._authState.next(data);

		// Avoid guest+logged limbo: interceptor falls back to guestToken and the
		// shell looks like a visitor while account APIs still use the user token.
		this.localStorageService.remove('guestToken');

		const persistRememberMe = data.rememberMe;
		if (persistRememberMe) {
			this.sessionStorageService.remove('auth');
			this.localStorageService.set('auth', data);
		} else {
			this.localStorageService.remove('auth');
			this.sessionStorageService.set('auth', data);
		}

		return this.authState$;
	}

	logout() {
		const lastSeenUpdate = localStorage.getItem('lastSeenUpdate');

		this._authState.next({
			user: null,
			rememberMe: false,
			isAuthenticated: false,
		});

		localStorage.clear();
		sessionStorage.clear();

		if (lastSeenUpdate) {
			localStorage.setItem('lastSeenUpdate', lastSeenUpdate);
		}

		authNavigation.redirectToAuthPage();

		return this.authState$;
	}

	continueAsGuest() {
		const lastSeenUpdate = localStorage.getItem('lastSeenUpdate');

		this._authState.next({
			user: null,
			rememberMe: false,
			isAuthenticated: false,
		});

		localStorage.clear();
		sessionStorage.clear();

		if (lastSeenUpdate) {
			localStorage.setItem('lastSeenUpdate', lastSeenUpdate);
		}

		authNavigation.redirectToHomePage();

		return this.authState$;
	}

	setGuestToken(token: string) {
		this.localStorageService.set('guestToken', token);
	}

	/**
	 * Drop a stale session on bootstrap without hard-redirecting.
	 * AuthGuard can then send the user to /auth cleanly.
	 */
	private resolveInitialState(): IAuthState {
		const stored = this.authStateStorage;
		if (!stored) return INITIAL_STATE;

		if (stored.isAuthenticated && isRefreshTokenExpired(stored.user)) {
			this.localStorageService.remove('auth');
			this.sessionStorageService.remove('auth');
			return INITIAL_STATE;
		}

		return stored;
	}
}
